import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setSeo, resetSeo } from "@/lib/seo";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Globe2, Award, Users } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/lib/images";

const values = [
  { icon: ShieldCheck, n: 1 },
  { icon: Globe2, n: 2 },
  { icon: Award, n: 3 },
  { icon: Users, n: 4 },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  useEffect(() => {
    setSeo({ title: t("about.seoTitle"), description: t("about.seoDesc"), url: window.location.href });
    return () => resetSeo();
  }, [t, i18n.language]);
  return (
    <div className="min-h-screen">
      <Navbar />
      <section className="relative grain pt-36 pb-16 border-b border-border bg-secondary/40">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-orange-600">{t("about.kicker")}</p>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight text-primary">{t("about.h1")}</h1>
            <p className="mt-5 text-muted-foreground text-lg leading-relaxed">{t("about.lead")}</p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-2xl overflow-hidden card-soft ring-1 ring-border">
          <img src={IMAGES.trust} alt={t("about.imgAlt")} className="w-full h-80 object-cover" />
        </div>
        <div>
          <h2 className="font-heading text-3xl tracking-tight text-primary">{t("about.h2")}</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">{t("about.body")}</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {values.map((v) => (
              <div key={v.n} data-testid={`about-value-${v.n}`} className="rounded-xl border border-border bg-card p-5 card-soft">
                <v.icon className="h-5 w-5 text-orange-600" />
                <h3 className="mt-3 font-heading text-lg text-primary">{t(`about.v${v.n}t`)}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{t(`about.v${v.n}b`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="rounded-2xl bg-primary text-primary-foreground px-8 py-14 text-center grain">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">{t("about.ctaTitle")}</h2>
          <Button size="lg" className="mt-7 rounded-sm bg-orange-500 text-white hover:bg-orange-600 h-12 px-8" onClick={() => navigate("/company-formation")} data-testid="about-cta">
            {t("about.cta")} <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </section>
      <Footer />
    </div>
  );
}
